/* eslint-disable react-hooks/rules-of-hooks */

import { isNativeAddress } from '@spritz-finance/sdk'
import { BigNumber } from 'ethers'
import { Maybe, TokenBalance } from '../../../../../graphql/generated/graphql'
import { useWeb3Connection } from '../../../../../web3/Web3Provider'
import { useAllowanceContext } from '../allowance/AllowanceProvider'
import { useERC20Balance } from '../balance/useERC20Balance'
import { useGasSettings } from './useGasSettings'
import { useSpritzPayArgs } from './useSpritzPayArgs'
import { applyContractFunctionOverride, toReadableValue } from './utils'
import { useDirectPayContext } from '../context/DirectPayContext'

type ActionLogItem = {
    text: string
    isSuccess?: boolean
    isError?: boolean
    isRetriable?: boolean
}

const usePaymentValidation = (token: Maybe<TokenBalance>) => {
    const { currentAllowance } = useAllowanceContext()
    const getTokenBalance = useERC20Balance(token)
    const { tokenModule } = useDirectPayContext()

    const validatePayment = async (
        requiredInput: BigNumber,
        setActionLog: (log: ActionLogItem) => void
    ) => {
        if (!token) return false
        const formattedInput = toReadableValue(requiredInput, token.decimals)

        // Check user has allowance to cover payment
        if (tokenModule?.paymentRequiresAllowance() && requiredInput.gt(currentAllowance ?? 0)) {
            const formattedBalance = toReadableValue(
                currentAllowance ?? BigNumber.from(0),
                token?.decimals
            )
            setActionLog({
                text: `Whoops, looks like you haven't set enough allowance for this payment. The required amount for this transaction is ${formattedInput} ${token.symbol}, the allowance is currently ${formattedBalance}`,
                isError: true,
            })
            return false
        }

        const balance = await getTokenBalance()
        const formattedBalance = toReadableValue(balance, token.decimals)

        // Check user has balance to cover payment
        if (requiredInput.gt(balance)) {
            setActionLog({
                text: `The required amount for this transaction is ${formattedInput} ${token.symbol}, but there is only ${formattedBalance} available`,
                isError: true,
            })
            return false
        }

        return true
    }

    return validatePayment
}

export const usePay = (token: Maybe<TokenBalance>) => {
    const { connectedNetwork } = useWeb3Connection()
    const { tokenModule } = useDirectPayContext()
    const validatePayment = usePaymentValidation(token)

    if (!connectedNetwork)
        return {
            pay: () => false,
            payState: undefined,
        }

    const sourceTokenAddress = tokenModule?.sourceTokenAddress() ?? '00000000'
    const { getGasOverride } = useGasSettings()
    const { paymentFunction, contractPayFunction, getPaymentArgs } =
        useSpritzPayArgs(sourceTokenAddress)

    const pay = async (
        amount: number,
        paymentReference: string,
        currentTime: Date,
        setActionLog: (log: ActionLogItem) => void
    ) => {
        setActionLog({
            text: 'Initiating transaction',
            isSuccess: true,
        })

        const gasOverride = await getGasOverride(paymentFunction)

        const { args, additionalHops, requiredTokenInput } = await getPaymentArgs(
            amount,
            paymentReference,
            currentTime
        )

        const valid = await validatePayment(requiredTokenInput, setActionLog)
        if (!valid) return

        const contractArgs = applyContractFunctionOverride(args, gasOverride, additionalHops)
        console.log(`Contract Args for ${paymentFunction}: `, contractArgs)

        //@ts-ignore
        await contractPayFunction.send(...contractArgs)
    }

    return {
        pay,
        payState: contractPayFunction.state,
        method: paymentFunction,
    }
}
